import { gql, useLazyQuery, useMutation } from '@apollo/client';
import { WorkflowObjectTypes } from '@generated/graphql';
import {
  isPrepareFormCompletionsRetryCancelled,
  prepareFormCompletionsWithLockRetry,
} from '@ovComponents/4-module/workflowCompletion/subSteps/prepareFormCompletions/prepareFormCompletionsWithLockRetry';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Typography,
} from 'hephaestus/web';
import { Refresh } from 'hephaestus/webIcons';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../../hooks/useLocalization';

const PREPARE_FORM_COMPLETIONS = gql`
  mutation prepareFormCompletions($input: PrepareFormCompletionsInput!) {
    prepareFormCompletions(input: $input) {
      formCompletions {
        id
        state
      }
    }
  }
`;

const FETCH_HOUSEHOLD_PRIMARY_USER_FOR_FORMS = gql`
  query fetchHouseholdPrimaryUser($id: SafeString!) {
    fetchClientGroup(input: { clientGroupId: $id }) {
      clientGroup {
        primaryUser {
          id: stringId
        }
      }
    }
  }
`;

export const PrepareFormCompletions = ({
  onNext,
  onBack,
  workflowCompletion,
  userId,
  direction,
}: {
  onNext: () => void;
  onBack: () => void;
  workflowCompletion: any;
  userId?: string;
  direction: 'FORWARD' | 'BACKWARD';
}) => {
  const { t } = useTranslation('workflowCompletions');
  const { translateBackendString } = useLocalization();
  const { workflowData } = useContext(WorkflowContext);
  const [error, setError] = useState<Error | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const onNextRef = useRef(onNext);
  const onBackRef = useRef(onBack);
  const hasAttemptedRef = useRef(false);
  const hasAdvancedRef = useRef(false);
  const cancelledRef = useRef(false);

  onNextRef.current = onNext;
  onBackRef.current = onBack;

  const advance = useCallback(() => {
    if (hasAdvancedRef.current) {
      return;
    }
    hasAdvancedRef.current = true;
    onNextRef.current();
  }, []);

  const [prepareFormCompletions] = useMutation(PREPARE_FORM_COMPLETIONS, {
    onCompleted: () => {
      if (cancelledRef.current) {
        return;
      }
      setError(null);
      advance();
    },
  });

  useEffect(
    () => () => {
      cancelledRef.current = true;
    },
    [],
  );

  const [fetchHouseholdPrimaryUser] = useLazyQuery(
    FETCH_HOUSEHOLD_PRIMARY_USER_FOR_FORMS,
    { fetchPolicy: 'no-cache' },
  );

  const isHousehold =
    workflowCompletion.objectType === WorkflowObjectTypes.Household;

  // For HOUSEHOLD workflows objectId is a ClientGroup id, but the mutation
  // needs a real user, so resolve the household's primary user instead.
  const resolveUserId = useCallback(async (): Promise<string | undefined> => {
    if (!isHousehold) {
      return userId;
    }
    const { data, error: lookupError } = await fetchHouseholdPrimaryUser({
      variables: {
        id: workflowCompletion.objectId,
        skipErrorHandler: true,
      },
    });
    // The execute promise resolves on GraphQL/network failures instead of
    // rejecting, so without this a failed lookup would read as a household
    // that has no primary user.
    if (lookupError) {
      throw lookupError;
    }
    return data?.fetchClientGroup?.clientGroup?.primaryUser?.id;
  }, [
    fetchHouseholdPrimaryUser,
    isHousehold,
    userId,
    workflowCompletion.objectId,
  ]);

  const attemptPrepareFormCompletions = useCallback(async () => {
    const formMapping = (
      workflowData.formMapping ?? workflowCompletion.context?.formMapping
    )?.filter(
      (mapping: { objectId: string | null }) => mapping.objectId !== null,
    );

    if (!workflowCompletion.organization?.id) {
      return;
    }

    if (!formMapping?.length) {
      advance();
      return;
    }

    setIsLoading(true);
    setError(null);

    // Set by RegenerateForms when the user chose to cancel the current signing
    // package and regenerate. Clears itself naturally on the next
    // setActiveWorkflowCompletion call (e.g. when nextStep advances).
    const forcePrepare = Boolean(
      workflowCompletion.forcePrepareFormCompletions,
    );

    try {
      const userId = await resolveUserId();
      // Only a household can resolve to no user without the lookup failing;
      // elsewhere objectId is the user, so let the mutation report on it.
      if (isHousehold && !userId) {
        throw new Error(
          t('prepareFormCompletions.householdPrimaryUserNotFound'),
        );
      }

      // If another prepare holds the FormPreparationLock, keep the spinner and
      // retry until the lock clears (or a real error / max attempts). Avoids the
      // sticky "already in progress" error from IMPCAC-217.
      await prepareFormCompletionsWithLockRetry(
        translateBackendString,
        () =>
          prepareFormCompletions({
            variables: {
              input: {
                workflowCompletionId: workflowCompletion.id,
                userId,
                organizationId: workflowCompletion.organization.id,
                formMapping,
                ...(forcePrepare && { forcePrepare: true }),
              },
              skipErrorHandler: true,
            },
          }),
        { isCancelled: () => cancelledRef.current },
      );
      return;
    } catch (err) {
      if (isPrepareFormCompletionsRetryCancelled(err)) {
        return;
      }
      setError(err as Error);
    }
    setIsLoading(false);
  }, [
    workflowCompletion,
    workflowData.formMapping,
    prepareFormCompletions,
    resolveUserId,
    isHousehold,
    advance,
    t,
  ]);

  useEffect(() => {
    if (hasAttemptedRef.current) {
      return;
    }
    hasAttemptedRef.current = true;
    // When the user navigates BACKWARD into this step, skip the (now-instant)
    // idempotent prepare mutation and continue going back. Otherwise the
    // on-mount mutation auto-advances onNext, which would either silently
    // bounce the user forward again or race the user's next back-press and
    // produce a Mongoose VersionError on the WorkflowCompletion document.
    // Same pattern as SynchronousIntegration / depositType.
    if (direction === 'BACKWARD') {
      onBackRef.current();
      return;
    }
    void attemptPrepareFormCompletions();
  }, [attemptPrepareFormCompletions, direction]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        minHeight: '300px',
      }}
    >
      <Typography variant='headingMedium'>
        {t('prepareFormCompletions.title')}
      </Typography>
      <Typography variant='bodyMedium' sx={{ mb: 4, fontWeight: 500 }}>
        {t('prepareFormCompletions.description')}
      </Typography>
      {error && (
        <Box
          sx={{
            mb: 2,
          }}
        >
          <Alert severity='error'>{error.message}</Alert>
          <Box
            sx={{
              mt: 2,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Button
              onClick={attemptPrepareFormCompletions}
              disabled={isLoading}
              variant='text'
              startIcon={<Refresh />}
            >
              {t('shared:retry')}
            </Button>
          </Box>
        </Box>
      )}
      {isLoading && <CircularProgress />}
    </Box>
  );
};
